import { FC, useMemo } from "react";
import { graphql, useLazyLoadQuery } from "react-relay";

import { SettingsPageContainer_Query } from "~/src/__relay_artifacts__/SettingsPageContainer_Query.graphql";
import { ExcludedIPListTabContainer } from "~/src/components/features/settings/ExcludedIPListTab";
import { FunnelListTabContainer } from "~/src/components/features/settings/FunnelListTab";
import {
  LegacyScriptTagSettingTabContainer,
  ScriptTagSettingTabContainer,
} from "~/src/components/features/settings/ScriptTagSettingTab";
import { SourceListTabContainer } from "~/src/components/features/settings/SourceListTab";
import { useTabs } from "~/src/lib/hooks";
import { useSettingsPageParams } from "~/src/routes/hooks";

import { ScheduleListTabContainer } from "../../features/settings/ScheduleListTab";
import { UrlMappingListTabContainer } from "../../features/settings/UrlMappingListTab";

import { SettingsPage } from "./presentations";

const query = graphql`
  query SettingsPageContainer_Query($siteSlug: String!) {
    viewer {
      role
      ...FunnelListTabContainer_user
    }
    site(slug: $siteSlug) {
      ...FunnelListTabContainer_site
      ...ExcludedIPListTabContainer_site
      ...ScriptTagSettingTabContainer_site
      ...LegacyScriptTagSettingTabContainer_site
      owner {
        jsTagManagementEnabled
        ...ScheduleListTabContainer_user
        ...UrlMappingListTabContainer_user
      }
    }
  }
`;

export const clientTabs = [
  { id: "funnels", label: "ファネル" },
  { id: "source", label: "流入元" },
  { id: "excluded_ip", label: "除外IP" },
  { id: "schedules", label: "スケジュール" },
  { id: "url_mappings", label: "URLマッピング" },
] as const;

const scriptTagTab = { id: "script_tag", label: "タグ" } as const;

export const SettingsPageContainer: FC<
  React.PropsWithChildren<React.PropsWithChildren<unknown>>
> = () => {
  const { siteSlug } = useSettingsPageParams();
  // Revalidate on every visit so enabling JS tag management (which seeds
  // modules server-side) is not stuck on a pre-enable empty Site fragment.
  const { site, viewer } = useLazyLoadQuery<SettingsPageContainer_Query>(
    query,
    {
      siteSlug,
    },
    { fetchPolicy: "store-and-network" }
  );
  const isAdmin = viewer.role === "ADMIN" || viewer.role === "SUPER";
  const jsTagManagementEnabled = site.owner.jsTagManagementEnabled;
  // Module UI when enabled for any account; legacy tag tab remains admin-only when off.
  const showScriptTagTab = jsTagManagementEnabled || isAdmin;
  const tabs = useMemo(
    () => (showScriptTagTab ? [...clientTabs, scriptTagTab] : [...clientTabs]),
    [showScriptTagTab]
  );
  const { currentTabId, onTabChange } = useTabs(tabs);

  return (
    <SettingsPage
      tabs={tabs}
      currentTabId={currentTabId}
      onTabChange={onTabChange}
    >
      {currentTabId === "funnels" && (
        <FunnelListTabContainer siteRef={site} userRef={viewer} />
      )}
      {currentTabId === "source" && (
        <SourceListTabContainer siteSlug={siteSlug} />
      )}
      {currentTabId === "excluded_ip" && (
        <ExcludedIPListTabContainer siteRef={site} />
      )}
      {currentTabId === "schedules" && (
        <ScheduleListTabContainer userRef={site.owner} />
      )}
      {currentTabId === "url_mappings" && (
        <UrlMappingListTabContainer userRef={site.owner} />
      )}
      {currentTabId === "script_tag" &&
        showScriptTagTab &&
        (jsTagManagementEnabled ? (
          <ScriptTagSettingTabContainer siteRef={site} />
        ) : (
          <LegacyScriptTagSettingTabContainer siteRef={site} />
        ))}
    </SettingsPage>
  );
};
